import { useState } from 'react';
import { VegaFlex, VegaPageNotification } from '@globalpayments/vega-react';

import PageSkeleton from '../../components/pageSkeleton';
import { useHorizonOptOutTracker } from '../../hooks/useHorizonOptOutTracker';
import { ALL_HORIZON_REGIONS } from '../../types/horizonOptOutTracker';
import type { HorizonOptOutDateRange, HorizonRegionFilter } from '../../types/horizonOptOutTracker';
import { getDefaultHorizonOptOutDateRange } from '../../utility/horizonOptOutTracker';
import { HorizonOptOutFilters } from './components/HorizonOptOutFilters';
import { HorizonOptOutSummary } from './components/HorizonOptOutSummary';
import { HorizonOptOutTable } from './components/HorizonOptOutTable';

/** Displays disqualified Horizon applications and their opt-out events for a selected date range. */
const HorizonOptOutTracker = () => {
  const [applicationSearch, setApplicationSearch] = useState('');
  const [draftApplicationSearch, setDraftApplicationSearch] = useState('');
  const [dateRange, setDateRange] = useState<HorizonOptOutDateRange>(getDefaultHorizonOptOutDateRange);
  const [draftDateRange, setDraftDateRange] = useState<HorizonOptOutDateRange>(dateRange);
  const [optOutReason, setOptOutReason] = useState('');
  const [draftOptOutReason, setDraftOptOutReason] = useState('');
  const [region, setRegion] = useState<HorizonRegionFilter>(ALL_HORIZON_REGIONS);
  const [draftRegion, setDraftRegion] = useState<HorizonRegionFilter>(ALL_HORIZON_REGIONS);
  const isDateRangeInvalid =
    !draftDateRange.startDate || !draftDateRange.endDate || draftDateRange.startDate > draftDateRange.endDate;
  const { data = [], isEnriching, isError, isLoading } = useHorizonOptOutTracker(dateRange, region);
  const normalizedApplicationSearch = applicationSearch.trim().toLowerCase();
  const normalizedOptOutReason = optOutReason.trim().toLowerCase();
  const filteredData = data.filter(
    (row) =>
      (!normalizedApplicationSearch ||
        [row.appUid, row.email, row.companyName].some((value) =>
          value.toLowerCase().includes(normalizedApplicationSearch),
        )) &&
      (!normalizedOptOutReason || row.optOutReason?.toLowerCase().includes(normalizedOptOutReason)),
  );

  const applyFilters = () => {
    setApplicationSearch(draftApplicationSearch);
    setDateRange(draftDateRange);
    setOptOutReason(draftOptOutReason);
    setRegion(draftRegion);
  };

  if (isLoading) return <PageSkeleton label="Loading Horizon opt-outs" variant="table" />;
  if (isError) {
    return (
      <VegaPageNotification
        duration={0}
        message="The Horizon opt-out data could not be loaded. Try again later."
        notificationTitle="Unable to load opt-outs"
        type="error"
      />
    );
  }

  return (
    <VegaFlex direction="col" gap="size-20">
      <HorizonOptOutSummary isEnriching={isEnriching} rows={filteredData} />
      <HorizonOptOutFilters
        applicationSearch={draftApplicationSearch}
        dateRange={draftDateRange}
        isInvalid={isDateRangeInvalid}
        onApply={applyFilters}
        onApplicationSearchChange={setDraftApplicationSearch}
        onDateRangeChange={setDraftDateRange}
        onOptOutReasonChange={setDraftOptOutReason}
        onRegionChange={setDraftRegion}
        optOutReason={draftOptOutReason}
        region={draftRegion}
      />
      <HorizonOptOutTable
        dateRange={dateRange}
        hasSearchFilter={normalizedApplicationSearch.length > 0 || normalizedOptOutReason.length > 0}
        isEnriching={isEnriching}
        rows={filteredData}
      />
    </VegaFlex>
  );
};

export default HorizonOptOutTracker;
